import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { OrderedMap } from "immutable";
import fetch from "logic/rest/FetchProvider";
import URLUtils from "util/URLUtils";
import useUserDateTime from "hooks/useUserDateTime";
import {
  isDateAttribute,
  isAttributeWithFilterOptions,
  isAttributeWithRelatedCollection
} from "components/common/EntityFilters/helpers/AttributeIdentification";
import { timeRangeTitle } from "components/common/EntityFilters/helpers/timeRange";
import { defaultOnError } from "util/conditional/onError";
const _collectionsByAttributeId = (attributesMetaData) => attributesMetaData == null ? void 0 : attributesMetaData.reduce((col, { id, related_collection }) => {
  if (!related_collection) {
    return col;
  }
  return {
    ...col,
    [id]: related_collection
  };
}, {});
const _identifierMetaByAttributeId = (attributesMetaData) => attributesMetaData == null ? void 0 : attributesMetaData.reduce(
  (col, { id, related_collection, related_identifier, type, related_display_fields, related_display_template }) => {
    if (!related_collection) {
      return col;
    }
    return {
      ...col,
      [id]: {
        identifier_field: related_identifier,
        identifier_type: related_identifier ? type : void 0,
        display_fields: related_display_fields,
        display_template: related_display_template
      }
    };
  },
  {}
);
const _urlQueryFiltersWithoutTitle = (filters, collectionsByAttributeId) => filters.entrySeq().reduce((col, [attributeId, filterValues]) => {
  const relatedCollection = collectionsByAttributeId == null ? void 0 : collectionsByAttributeId[attributeId];
  if (!relatedCollection) {
    return col;
  }
  return col.set(attributeId, filterValues);
}, OrderedMap());
const filtersWithoutTitlePayload = (filtersWithoutTitle, collectionsByAttributeId, identifierMetaByAttributeId) => ({
  entities: filtersWithoutTitle.entrySeq().reduce((col, [attributeId, filterValues]) => {
    const relatedCollection = collectionsByAttributeId[attributeId];
    const meta = identifierMetaByAttributeId[attributeId];
    return [
      ...col,
      ...filterValues.map((value) => {
        var _a;
        return {
          id: value,
          type: relatedCollection,
          ...(meta == null ? void 0 : meta.identifier_field) && { identifier_field: meta.identifier_field },
          ...(meta == null ? void 0 : meta.identifier_type) && { identifier_type: meta.identifier_type },
          ...((_a = meta == null ? void 0 : meta.display_fields) == null ? void 0 : _a.length) && { display_fields: meta.display_fields },
          ...(meta == null ? void 0 : meta.display_template) && { display_template: meta.display_template }
        };
      })
    ];
  }, [])
});
const filterTitle = (attribute, requestedFilterTitles, filterValue, formatTime, notPermittedEntities, isErrorFetchingTitles) => {
  var _a, _b;
  if (isDateAttribute(attribute)) {
    return timeRangeTitle(filterValue, formatTime);
  }
  if (isAttributeWithFilterOptions(attribute)) {
    const relatedOption = attribute.filter_options.find(({ value }) => value === filterValue);
    return (_a = relatedOption == null ? void 0 : relatedOption.title) != null ? _a : filterValue;
  }
  if (isAttributeWithRelatedCollection(attribute)) {
    if (isErrorFetchingTitles || (notPermittedEntities == null ? void 0 : notPermittedEntities.includes(filterValue))) {
      return filterValue;
    }
    const fetchedTitle = (_b = requestedFilterTitles == null ? void 0 : requestedFilterTitles.find(
      ({ id, type }) => type === attribute.related_collection && id === filterValue
    )) == null ? void 0 : _b.title;
    return fetchedTitle != null ? fetchedTitle : "Loading...";
  }
  return filterValue;
};
const _allFiltersWithTitle = (urlQueryFilters, requestedFilterTitles, attributesMetaData, formatTime, notPermittedEntities, isErrorFetchingTitles) => urlQueryFilters.entrySeq().reduce((col, [attributeId, filterValues]) => {
  const relatedAttribute = attributesMetaData == null ? void 0 : attributesMetaData.find(({ id }) => id === attributeId);
  if (!relatedAttribute) {
    throw new Error(
      `Found value for attribute "${attributeId}", which is not in list of registered attributes: ${attributesMetaData == null ? void 0 : attributesMetaData.map(({ id }) => id).join(", ")} - typo in attribute name?`
    );
  }
  const filtersWithTitle = filterValues.map((value) => {
    const title = filterTitle(
      relatedAttribute,
      requestedFilterTitles,
      value,
      formatTime,
      notPermittedEntities,
      isErrorFetchingTitles
    );
    return {
      title,
      value
    };
  });
  return col.set(attributeId, filtersWithTitle);
}, OrderedMap());
const filtersWithTitleToResponse = (filtersWithTitle, attributesMetaData) => filtersWithTitle.entrySeq().reduce((col, [_attributeId, filters]) => {
  var _a;
  const relatedCollection = (_a = attributesMetaData == null ? void 0 : attributesMetaData.find(({ id }) => id === _attributeId)) == null ? void 0 : _a.related_collection;
  if (!relatedCollection) {
    return col;
  }
  return [...col, ...filters.map(({ value, title }) => ({ id: value, type: relatedCollection, title }))];
}, []);
const fetchFilterTitles = (payload) => fetch("POST", URLUtils.qualifyUrl("/system/catalog/entities/titles"), payload);
const useFiltersWithTitle = (urlQueryFilters, attributesMetaData, enabled = true) => {
  var _a;
  const queryClient = useQueryClient();
  const { formatTime } = useUserDateTime();
  const collectionsByAttributeId = _collectionsByAttributeId(attributesMetaData);
  const identifierMetaByAttributeId = _identifierMetaByAttributeId(attributesMetaData);
  const urlQueryFiltersWithoutTitle = _urlQueryFiltersWithoutTitle(urlQueryFilters, collectionsByAttributeId);
  const payload = filtersWithoutTitlePayload(
    urlQueryFiltersWithoutTitle,
    collectionsByAttributeId,
    identifierMetaByAttributeId
  );
  const { data, isInitialLoading, isError } = useQuery({
    queryKey: ["entity_titles", payload],
    queryFn: () => defaultOnError(fetchFilterTitles(payload), "Loading filter titles failed with status", "Could not load streams"),
    placeholderData: keepPreviousData,
    enabled: enabled && !!payload.entities.length
  });
  const cachedResponse = queryClient.getQueryData(["entity_titles", payload]);
  const requestedFilterTitles = (_a = cachedResponse != null ? cachedResponse : data) == null ? void 0 : _a.entities;
  const allFiltersWithTitle = _allFiltersWithTitle(
    urlQueryFilters,
    requestedFilterTitles,
    attributesMetaData,
    formatTime,
    data == null ? void 0 : data.not_permitted_to_view,
    isError
  );
  const onChange = useCallback(
    (newFiltersWithTitle, newUrlQueryFilters) => {
      const newURLQueryFiltersWithoutTitle = _urlQueryFiltersWithoutTitle(newUrlQueryFilters, collectionsByAttributeId);
      const newPayload = filtersWithoutTitlePayload(
        newURLQueryFiltersWithoutTitle,
        collectionsByAttributeId,
        identifierMetaByAttributeId
      );
      const newResponse = filtersWithTitleToResponse(newFiltersWithTitle, attributesMetaData);
      queryClient.setQueryData(["entity_titles", newPayload], { entities: newResponse });
    },
    [attributesMetaData, collectionsByAttributeId, identifierMetaByAttributeId, queryClient]
  );
  return {
    data: allFiltersWithTitle,
    isInitialLoading,
    onChange
  };
};
export default useFiltersWithTitle;
